import { createFileRoute, Link, Outlet, useRouterState } from '@tanstack/react-router';
import { ChevronLeft } from 'lucide-react';

import { Alert, Card, cn, PageHeader, Spinner } from '@platform/ui';

import { TenantStatusBadge } from '../../components/status-badges';
import { useTenantDetail } from '../../features/tenants/use-tenant';

export const Route = createFileRoute('/tenants/$tenantId')({
  component: TenantLayout,
});

/**
 * `/tenants/$tenantId` — the shell every tenant tab renders inside (FR-SUPER-02).
 *
 * The tabs are routes, not local state, so a support engineer can send a colleague a link
 * to the Domains tab of one clinic. They are marked up as navigation with
 * `aria-current="page"` rather than as an ARIA tablist: these change the URL and load a
 * different screen, which is a link's job, not a tab's (rules §48).
 */
const TABS = [
  { to: '/tenants/$tenantId', segment: '', label: 'Overview' },
  { to: '/tenants/$tenantId/domains', segment: '/domains', label: 'Domains' },
  { to: '/tenants/$tenantId/branding', segment: '/branding', label: 'Identity & branding' },
  { to: '/tenants/$tenantId/features', segment: '/features', label: 'Plan & features' },
  { to: '/tenants/$tenantId/integrations', segment: '/integrations', label: 'Integrations' },
  { to: '/tenants/$tenantId/users', segment: '/users', label: 'Users' },
] as const;

function TenantLayout() {
  const { tenantId } = Route.useParams();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const tenant = useTenantDetail(tenantId);

  if (tenant.isPending) {
    return (
      <Card className="p-6">
        <Spinner label="Loading tenant" />
      </Card>
    );
  }

  if (tenant.isError) {
    return <Alert tone="error">That tenant could not be loaded.</Alert>;
  }

  const summary = tenant.data.tenant;

  return (
    <>
      <PageHeader
        eyebrow={
          <Link to="/tenants" className="inline-flex items-center gap-1 hover:underline">
            <ChevronLeft size={14} strokeWidth={2} aria-hidden="true" />
            All tenants
          </Link>
        }
        title={summary.name}
        description={
          <span className="flex flex-wrap items-center gap-2">
            <code>{summary.slug}</code>
            <span aria-hidden="true">·</span>
            <span>{summary.planName}</span>
            <span aria-hidden="true">·</span>
            <span>{summary.timeZone}</span>
          </span>
        }
        actions={<TenantStatusBadge status={summary.status} />}
      />

      <nav aria-label="Tenant sections" className="mb-6 border-b border-neutral-200">
        <ul className="-mb-px flex flex-wrap gap-1">
          {TABS.map((tab) => {
            /* The active class is computed rather than handed to `activeProps`, so that
               `cn` (tailwind-merge) resolves the border colour deterministically instead
               of leaving two border utilities to fight over CSS source order. */
            const active = pathname.replace(/\/$/, '') === `/tenants/${tenantId}${tab.segment}`;
            return (
              <li key={tab.to}>
                <Link
                  to={tab.to}
                  params={{ tenantId }}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    'inline-block border-b-2 px-4 py-3 text-base font-medium',
                    active
                      ? 'border-primary text-primary-accessible'
                      : 'border-transparent text-neutral-500 hover:text-ink',
                  )}
                >
                  {tab.label}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      <Outlet />
    </>
  );
}
